"use client";

import * as React from "react";
import { cn } from "../../lib/utils";
import { SideOverlayDrawer } from "./side-overlay-drawer";

export interface NotificationFilter {
  id: string;
  label: string;
}

export interface NotificationItem {
  id: string;
  title: string;
  description: string;
  tone?: "highlight" | "default";
}

export interface NotificationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  filters: NotificationFilter[];
  activeFilterId: string;
  onFilterChange: (id: string) => void;
  notifications: NotificationItem[];
  onMenuClick?: () => void;
}

export function NotificationDrawer({
  isOpen,
  onClose,
  filters,
  activeFilterId,
  onFilterChange,
  notifications,
  onMenuClick,
}: NotificationDrawerProps) {
  return (
    <SideOverlayDrawer
      isOpen={isOpen}
      onClose={onClose}
      onBack={onClose}
      title="Notification"
      headerAction={
        <button
          type="button"
          onClick={onMenuClick}
          aria-label="Notification options"
          className="flex h-8 w-8 items-center justify-center rounded-md text-[#98A2B3]"
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M9 9.75C9.41421 9.75 9.75 9.41421 9.75 9C9.75 8.58579 9.41421 8.25 9 8.25C8.58579 8.25 8.25 8.58579 8.25 9C8.25 9.41421 8.58579 9.75 9 9.75Z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M9 4.5C9.41421 4.5 9.75 4.16421 9.75 3.75C9.75 3.33579 9.41421 3 9 3C8.58579 3 8.25 3.33579 8.25 3.75C8.25 4.16421 8.58579 4.5 9 4.5Z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M9 15C9.41421 15 9.75 14.6642 9.75 14.25C9.75 13.8358 9.41421 13.5 9 13.5C8.58579 13.5 8.25 13.8358 8.25 14.25C8.25 14.6642 8.58579 15 9 15Z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      }
    >
      <div className="flex text-nowrap overflow-x-auto gap-2">
        {filters.map((filter) => {
          const isActive = filter.id === activeFilterId;
          return (
            <button
              key={filter.id}
              type="button"
              onClick={() => onFilterChange(filter.id)}
              className={cn(
                "rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors",
                isActive
                  ? "border-[#0D41E1] bg-white text-[#0D41E1]"
                  : "border-black bg-white text-[#475467]"
              )}
            >
              {filter.label}
            </button>
          );
        })}
      </div>

      <div className="mt-6 space-y-3">
        {notifications.map((notification) => {
          const isHighlight = notification.tone === "highlight";
          return (
            <div
              key={notification.id}
              className={cn(
                "rounded-xl border px-3 py-3",
                isHighlight ? "border-[#0D41E1] bg-white" : "border-[#D9D9D9] bg-white"
              )}
            >
              <p className="text-sm font-semibold text-[#344054]">{notification.title}</p>
              <p className="mt-1 text-sm text-[#667085]">{notification.description}</p>
            </div>
          );
        })}
      </div>
    </SideOverlayDrawer>
  );
}
